import { InteractionType } from '@azure/msal-browser';
import { useIsAuthenticated, useMsalAuthentication } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { type FC, type ReactElement, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { getAllSubscriptions } from '../api/subscription';
import { TroubleshootingSteps } from '../components/TroubleshootingSteps';
import { UserHeader } from '../components/UserHeader';
import { useMarketplaceToken } from '../hooks/useMarketplaceToken';
import { useMsalIdToken } from '../hooks/useMsalIdToken';
import { useResolveMarketplaceToken } from '../hooks/useResolveMarketplaceToken';

const SubscriptionList: FC = (): ReactElement => {
  const navigate = useNavigate();
  const marketplaceToken = useMarketplaceToken();

  // Initialize MSAL authentication - automatically redirects if not authenticated
  const isAuthenticated = useIsAuthenticated();
  useMsalAuthentication(InteractionType.Redirect, {
    scopes: ['openid', 'profile', 'email'],
  });

  const msalToken = useMsalIdToken();

  // Resolve marketplace token to get subscription ID
  const tokenResolution = useResolveMarketplaceToken(msalToken, marketplaceToken);

  // Redirect to subscription detail page once token is resolved
  useEffect(() => {
    if (tokenResolution.subscriptionId) {
      navigate(`/subscription/${tokenResolution.subscriptionId}`);
    }
  }, [tokenResolution.subscriptionId, navigate]);

  const subscriptionsQuery = useQuery({
    enabled: !!msalToken && !marketplaceToken,
    queryKey: ['subscriptions'],
    queryFn: () => (msalToken ? getAllSubscriptions(msalToken) : undefined),
  });

  const subscriptions = subscriptionsQuery.data?.data.subscriptions ?? [];

  // Show loading state while authentication is in progress
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          <p className="text-sm text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Show loading state while resolving marketplace token
  if (tokenResolution.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="w-full max-w-2xl rounded-lg bg-white p-10 shadow-md">
          <UserHeader />
          <div className="py-8">
            <h1 className="mb-8 text-center text-3xl font-bold text-gray-900">Activate Subscription</h1>
            <div className="flex flex-col items-center gap-4">
              <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <p className="text-base text-gray-600">Resolving subscription details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if token resolution failed
  if (tokenResolution.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="w-full max-w-2xl rounded-lg bg-white p-10 shadow-md">
          <UserHeader />
          <div>
            <h1 className="mb-6 text-3xl font-bold text-gray-900">Subscription Not Found</h1>
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
              <p className="mb-3 text-lg font-semibold">We couldn't identify this purchase.</p>
              <TroubleshootingSteps />
            </div>
            <div className="text-center">
              <Link
                to="/"
                className="inline-block rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                View All Subscriptions
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Subscribed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PendingFulfillmentStart':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Suspended':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Unsubscribed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PendingFulfillmentStart':
        return 'Pending';
      default:
        return status;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-lg bg-white p-10 shadow-md">
          <UserHeader />

          <h1 className="mb-2 text-2xl font-semibold text-gray-900">Your Subscriptions</h1>
          <p className="mb-6 text-sm text-gray-600">Manage your Microsoft Marketplace subscriptions</p>

          {/* Loading state */}
          {subscriptionsQuery.isLoading && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <p className="text-sm text-gray-600">Loading subscriptions...</p>
            </div>
          )}

          {/* Error state */}
          {subscriptionsQuery.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
              <p className="font-medium">Failed to load subscriptions</p>
              <TroubleshootingSteps />
            </div>
          )}

          {/* Empty state */}
          {!subscriptionsQuery.isLoading && !subscriptionsQuery.isError && subscriptions.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
              <p className="text-gray-600">You don't have any subscriptions yet.</p>
            </div>
          )}

          {/* Subscriptions list */}
          {subscriptions.length > 0 && (
            <div className="space-y-4">
              {subscriptions.map((subscription) => (
                <div
                  key={subscription.id}
                  className="rounded-lg border border-gray-200 p-4 transition-colors hover:border-indigo-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-3">
                        <Link
                          to={`/subscription/${subscription.id}`}
                          className="text-lg font-medium text-gray-900 transition-colors hover:text-indigo-600"
                        >
                          {subscription.name}
                        </Link>
                        <span
                          className={`rounded-full border px-2 py-1 text-xs font-medium ${getStatusBadgeClass(subscription.saasSubscriptionStatus)}`}
                        >
                          {getStatusLabel(subscription.saasSubscriptionStatus)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                        <div>
                          <p className="text-gray-500">Start Date</p>
                          <p className="font-medium text-gray-900">{formatDate(subscription.term.startDate)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">End Date</p>
                          <p className="font-medium text-gray-900">{formatDate(subscription.term.endDate)}</p>
                        </div>
                        {subscription.quantity && (
                          <div>
                            <p className="text-gray-500">Quantity</p>
                            <p className="font-medium text-gray-900">{subscription.quantity}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-500">Term</p>
                          <p className="font-medium text-gray-900">
                            {subscription.term.termUnit === 'P1M' ? 'Monthly' : 'Yearly'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/subscription/${subscription.id}`}
                      className="ml-4 text-sm font-medium whitespace-nowrap text-indigo-600 hover:text-indigo-800"
                    >
                      View Details →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(SubscriptionList);
